// =====================================================================
// tpCustomer — MEDDPICCR derivation (P1.1 + P1.2 + P1.3)
//
// Pure module. Same shape as opportunity-commission.js — exports its
// public surface on window.tpMeddpiccr and is therefore directly
// require()-able from node under a `global.window={}` shim, so the
// self-test in opportunities-tests.js can hit it.
//
// Source of truth: tpcustomer_meddpiccr_build_spec.md §P1.1 / §P1.2 /
// §P1.3 plus the per-element confidence floors from
// tpcustomer_confidence_thresholds_task.md (recorded in
// MEDDPICCR-spec-deviations.md D-X.2 because that task file was deleted
// before any P1 code landed).
//
// Bridge mode (see deviations doc D-1.1.2): with P1.2 and P1.3 landed,
// M / DC / DP / R now derive from their structured fields. Only
// Identify Pain (I) still bridges to opp.meddpicc — the spec calls I
// "unchanged; keep sourcing from linked research/pain", and there is
// no structured pain field to replace the manual toggle with yet.
// =====================================================================

(function () {
  'use strict';

  // Per-element confidence floors. Pulled from settings.meddpiccThresholds
  // — the supplementary thresholds task settled on EB=3 (a fact you confirm)
  // and Champion=4 (behaviour the contact must earn; false-positive Champion
  // is the worst error in the model). The `?? 3` / `?? 4` fallback is
  // required: a missing setting must NOT silently disable derivation.
  function confidenceFloor(role, settings) {
    var cm = (settings && settings.meddpiccThresholds && settings.meddpiccThresholds.confidenceMin) || {};
    if (role === 'Economic Buyer') return Number.isInteger(cm.economicBuyer) ? cm.economicBuyer : 3;
    if (role === 'Champion')       return Number.isInteger(cm.champion)      ? cm.champion      : 4;
    return Infinity; // No other stakeholder role drives E or C.
  }

  // Schema stores legalReview / securityReview / procurementInvolved as
  // INTEGER DEFAULT 0 — there is no tri-state. "Resolved (not blank)" per
  // the spec is therefore treated as "= 1" for these booleans. See
  // deviations doc D-1.1.3.
  //
  // Reviewer (MEDDPICCR-reviewer-response.md D-1.1.3): accepted pragmatic.
  // A formally-answered "legal not required" cannot be distinguished from
  // "not yet assessed" today — P may under-derive on deals where reviews
  // were waived. If "waived vs not-assessed" becomes a real reporting
  // need, the three booleans should move to NULL/0/1 tri-state together
  // in one schema change. Tracked in MEDDPICCR-backlog.md.
  function isTruthy(v) {
    if (v === true) return true;
    if (v === null || v === undefined || v === false) return false;
    return Number(v) === 1;
  }

  function isNonEmptyString(v) {
    return typeof v === 'string' && v.trim().length > 0;
  }

  function competitorsNonEmpty(opp) {
    var c = opp && opp.competitors;
    if (Array.isArray(c)) return c.length > 0;
    // The opportunities serializer parses competitors as JSON in normal
    // flows; the string branch is defensive against a row that round-
    // tripped from the DB without going through the serializer.
    if (typeof c === 'string') {
      try {
        var parsed = JSON.parse(c);
        return Array.isArray(parsed) && parsed.length > 0;
      } catch (_) {
        return c.trim().length > 0;
      }
    }
    return false;
  }

  function paperProcessSatisfied(opp) {
    if (!opp) return false;
    if (!isTruthy(opp.legalReview))         return false;
    if (!isTruthy(opp.securityReview))      return false;
    if (!isTruthy(opp.procurementInvolved)) return false;
    if (!isNonEmptyString(opp.proposalSent)) return false;
    return isNonEmptyString(opp.contractStart)
        || isNonEmptyString(opp.contractSent)
        || isNonEmptyString(opp.contractSigned);
  }

  function hasStakeholderAtFloor(stakeholders, role, settings) {
    if (!Array.isArray(stakeholders)) return false;
    var floor = confidenceFloor(role, settings);
    for (var i = 0; i < stakeholders.length; i++) {
      var s = stakeholders[i];
      if (s && s.role === role && Number(s.roleConfidence) >= floor) return true;
    }
    return false;
  }

  // Bridge to existing manual toggle — only Identify Pain is left.
  // The spec calls I "unchanged" — there is no structured pain field
  // to replace this with today, so it stays a manual toggle.
  function bridgeValue(opp, key) {
    return !!(opp && opp.meddpicc && opp.meddpicc[key]);
  }

  // M (Metrics) derives from the opportunity's metrics JSON. The minimal
  // bar is "a metric has been named" — having a non-empty metricLabel
  // implies the rest of the structured fields (baseline / target / source)
  // can be filled in incrementally without flipping the score on/off.
  // See deviations doc D-1.2.x for the why.
  //
  // Reviewer LOCKED (MEDDPICCR-reviewer-response.md L3): keep label-only
  // derivation here. When the Commit gate is hardened (future P2.x),
  // M at Commit stage should additionally require baseline + target,
  // because a named outcome is not a quantified case. Tracked in
  // MEDDPICCR-backlog.md ("Commit-gate hardening for M").
  function metricsPopulated(opp) {
    var m = opp && opp.metrics;
    if (!m) return false;
    if (typeof m === 'string') {
      try { m = JSON.parse(m); } catch (_) { return false; }
    }
    if (!m || typeof m !== 'object' || Array.isArray(m)) return false;
    return isNonEmptyString(m.metricLabel);
  }

  // R (Risk) derives from opportunity_risks. The rule per spec: R ON only
  // when every open risk has non-empty owner + mitigation. An empty risk
  // register does NOT satisfy R — a non-trivial deal with zero risks
  // logged is "not yet assessed", not "all clear". See deviations doc
  // D-1.2.x.
  //
  // Reviewer LOCKED (MEDDPICCR-reviewer-response.md L1 + L2):
  //   L1 — Empty register stays false. No MEDDPICCR element goes green
  //        without evidence. Do not flip to vacuous-true.
  //   L2 — A row with absent/falsy status is treated as `open` and counts
  //        toward the requirement, matching the schema default.
  function risksSatisfied(risks) {
    if (!Array.isArray(risks) || risks.length === 0) return false;
    for (var i = 0; i < risks.length; i++) {
      var r = risks[i];
      if (!r) return false;
      // Only `open` status is gating; mitigated/accepted risks are
      // already-resolved entries that don't block the element.
      // Absent status defaults to open (matches schema default + L2).
      if (r.status && r.status !== 'open') continue;
      if (!isNonEmptyString(r.owner))      return false;
      if (!isNonEmptyString(r.mitigation)) return false;
    }
    return true;
  }

  // Internal: compute the pre-override (derived-only) booleans.
  function deriveRaw(opp, stakeholders, settings, risks) {
    return {
      metrics:          metricsPopulated(opp),
      economicBuyer:    hasStakeholderAtFloor(stakeholders, 'Economic Buyer', settings),
      // DC / DP: free-text fields, populated = non-empty after trim.
      decisionCriteria: isNonEmptyString(opp.decisionCriteria),
      decisionProcess:  isNonEmptyString(opp.decisionProcess),
      paperProcess:     paperProcessSatisfied(opp),
      identifyPain:     bridgeValue(opp, 'identifyPain'),
      champion:         hasStakeholderAtFloor(stakeholders, 'Champion', settings),
      competition:      competitorsNonEmpty(opp) && isNonEmptyString(opp.competitivePosition),
      risk:             risksSatisfied(risks),
    };
  }

  // Returns one rich entry per MEDDPICCR_KEYS slot:
  //   { value: bool, source: 'derived' | 'override-on' | 'override-off',
  //     by?: string, at?: string, reason?: string }
  //
  // Per the P1.4 spec ("the default path is derived, never a bare click"),
  // the source field is what surfaces in the UI as a visible override
  // marker. The bare boolean is read off `.value` — meddScoreFromDerived
  // does this so existing callers keep working without restructuring.
  //
  // Signature: deriveMeddpiccr(opp, stakeholders, settings, risks). risks
  // is the array of opportunity_risks rows for THIS deal — pass [] if
  // unknown; the call site in ui.jsx pre-filters from D.OPPORTUNITY_RISKS.
  function deriveMeddpiccr(opp, stakeholders, settings, risks) {
    opp = opp || {};
    stakeholders = stakeholders || [];
    risks = risks || [];
    var raw = deriveRaw(opp, stakeholders, settings, risks);
    var overrides = (opp.meddpiccOverrides && typeof opp.meddpiccOverrides === 'object')
      ? opp.meddpiccOverrides : {};
    var out = {};
    var keys = Object.keys(raw);
    for (var i = 0; i < keys.length; i++) {
      var k = keys[i];
      var ov = overrides[k];
      if (ov && (ov.source === 'override-on' || ov.source === 'override-off') && typeof ov.value === 'boolean') {
        out[k] = {
          value:  ov.value,
          source: ov.source,
          by:     ov.by,
          at:     ov.at,
          reason: ov.reason,
        };
      } else {
        out[k] = { value: raw[k], source: 'derived' };
      }
    }
    return out;
  }

  // Accepts either the rich shape (preferred — what deriveMeddpiccr now
  // returns) or the legacy bare-boolean map. The dual support keeps
  // older browser caches from breaking when a script-version mismatch
  // pairs a new deriver with stale call-site code.
  function meddScoreFromDerived(derived, keyOrder) {
    var keys = keyOrder || Object.keys(derived || {});
    var total = keys.length;
    var got = 0;
    for (var i = 0; i < keys.length; i++) {
      var entry = derived[keys[i]];
      var v = (entry && typeof entry === 'object' && 'value' in entry) ? entry.value : entry;
      if (v) got++;
    }
    return { got: got, total: total };
  }

  if (typeof window !== 'undefined') {
    window.tpMeddpiccr = {
      confidenceFloor: confidenceFloor,
      deriveMeddpiccr: deriveMeddpiccr,
      meddScoreFromDerived: meddScoreFromDerived,
    };
  }
})();
